import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
    TextField,
    Button,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Container,
    Typography,
    FormHelperText,
    CircularProgress ,

} from '@mui/material';
import { ArrowForward, ArrowBack, CheckCircle, CloseRounded } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import * as yup from 'yup';


const sectionSchemas = {
    1: yup.object().shape({
        nombre: yup.string().required("El nombre es obligatorio"),
        pri_apellido: yup.string().required("El primer apellido es obligatorio"),
        seg_apellido: yup.string().required("El segundo apellido es obligatorio"),
        tipo_doc: yup.string().required("El tipo de documento es obligatorio"),
        dni: yup.string().required("El DNI es obligatorio"),
        fecha_nacimiento: yup.string().required("La fecha de nacimiento es obligatoria"),

    }),
    2: yup.object().shape({
        telefono: yup.string().required("El teléfono es obligatorio"),
        email: yup.string().email('Email invalido').required("El email es obligatorio"),
        direccion: yup.string().required("La dirección es obligatoria"),
        cod_postal: yup.string().required("El código postal es obligatorio"),
        ciudad: yup.string().required("La ciudad es obligatoria"),
        provincia: yup.string().required("La provincia es obligatoria"),
    }),
    3: yup.object().shape({
        forma_de_pago: yup.string().required("La forma de pago es obligatoria"),
        // titular_IBAN: yup.string().required("El titular IBAN es obligatorio"),
        // IBAN: yup.string().required("El IBAN es obligatorio"),
    }),
    4: yup.object().shape({
        // persona_contacto: yup.string().required("La persona de contacto es obligatoria"),
        // telefono_contacto: yup.string().required("El teléfono de contacto es obligatorio"),
        // email_contacto: yup.string().email.required("El email de contacto es obligatorio"),
    }),
    5: yup.object().shape({
        carrera: yup.string().required("La carrera es obligatoria"),
        perfil: yup.string().required("El perfil es obligatorio"),
        curso: yup.number()
            .min(1, "El curso debe ser mayor o igual a 1")
            .max(6, "El curso debe ser menor o igual a 6")
            .required("El curso es obligatorio")

    }),
};


const useStyles = makeStyles((theme) => ({
    navigationContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: theme.spacing(3),
    },
    navCircle: {
        width: '35px',
        height: '35px',
        borderRadius: '50%',
        margin: '0 5px',
        cursor: 'pointer',
        backgroundColor: theme.palette.grey[300],
        '&:hover': {
            backgroundColor: theme.palette.grey[400],
        },
    },
    activeCircle: {
        backgroundColor: theme.palette.primary.main,
    },
    fullPageLoader: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
    errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: theme.spacing(3),
        border: '2px solid red',
        borderRadius: '8px',
        backgroundColor: 'white',
    },
    errorMessage: {
        marginTop: theme.spacing(2),
    },
    errorDetails: {
        marginTop: theme.spacing(2),
        textAlign: 'center',
    },
    successContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: theme.spacing(3),
        border: '2px solid green',
        borderRadius: '8px',
        backgroundColor: 'white',
    },
    successMessage: {
        marginTop: theme.spacing(2),
    },
}));


function MatriculaForm({ token, onError }) {
    const [alumnoData, setAlumnoData] = useState({});
    const [ciudadData, setCiudadData] = useState({});
    const [provinciaData, setProvinciaData] = useState({});
    const [perfilData, setPerfil] = useState('');
    const [carreraData, setCarrera] = useState('');


    const [errors, setErrors] = useState({});

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [currentSection, setCurrentSection] = useState(1);
    const classes = useStyles();

    const totalSections = 5;

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`https://intranet.academianext.es/alumnos/validate_token/?token=${token}`);
                
                //verificar que exista respuesta
                if (!response.data) {
                    setErrors({ submit: ["Error desconocido, ponte en contacto con nosotros"] });
                    setIsSubmitted(true);
                    return;
                }
                setAlumnoData(response.data['alumno']);
                setCiudadData(response.data['ciudades']);
                setProvinciaData(response.data['provincias']);
                setPerfil(response.data['perfiles']);
                setCarrera(response.data['tipos_carrera']);



            }catch (error) {
                    if (error.response) {
                        setErrors({ submit: error.response.data });
                    } else {
                        setErrors({ submit: ["Error desconocido al realizar la solicitud."] });
                    }
                    setIsSubmitted(true);
                }
                
        };
        fetchData();
    }, [token, onError]);

    const validateSection = (sectionData, sectionNumber) => {
        try {
            sectionSchemas[sectionNumber].validateSync(sectionData, { abortEarly: false });
            setErrors({});
            return true;
        } catch (error) {
            if (error instanceof yup.ValidationError) {
                const errorMessages = {};
                error.inner.forEach((err) => {
                    errorMessages[err.path] = err.message;
                });
                setErrors(errorMessages);
            }
            return false;
        }
    };

    const validateAllSections = (data) => {
        for (let i = 1; i <= totalSections; i++) {
            const currentSectionData = {};
            for (const key in sectionSchemas[i].fields) {
                currentSectionData[key] = data[key];
            }

            if (!validateSection(currentSectionData, i)) {
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (validateAllSections(alumnoData)) {
            setIsSubmitting(true);
            try {
                const response = await axios.post(`https://intranet.academianext.es/alumnos/validar_matricula/${token}/`, alumnoData);
                console.log(response.data);
                setIsSubmitted(true);
            } catch (error) {
                if (error.response.status === 400){
                    setErrors({ submit: error.response.data });
                }else {
                    setErrors({ submit: ["Error desconocido al enviar la matrícula." ]});
                }
                setIsSubmitted(true);
            }
            setIsSubmitting(false);
        }
    };




    const nextSection = () => {
        const currentSectionData = {}; // Recopila los datos de la sección actual
        for (const key in sectionSchemas[currentSection].fields) {
            currentSectionData[key] = alumnoData[key];
        }

        if (validateSection(currentSectionData, currentSection)) {
            setCurrentSection(prev => prev + 1);
        }
    };

    const prevSection = () => {
        setCurrentSection(prev => prev - 1);
    };

    const fanVariants = {
        hidden: { x: 300, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.8 } },
        exit: { x: -300, opacity: 0 }
    };

    if (isSubmitting) {
        return (
            <div className={classes.fullPageLoader}>
                <CircularProgress />
            </div>
        );
    }

    if (isSubmitted) {
        if (errors.submit) {
            return (
                <motion.div
                    key={`section${currentSection}`}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={fanVariants}
                >
                    <div className={classes.fullPageLoader}>
                        <div className={classes.errorContainer}>
                            <CloseRounded style={{ color: 'red', fontSize: 50 }} />
                            <Typography variant="h4" className={classes.errorMessage}>
                                Hubo un error al enviar la matrícula 😢
                            </Typography>
                            {errors.submit && (
                                <div className={classes.errorDetails}>
                                    {Object.entries(errors.submit).map(([field, errorMessage]) => (
                                        <Typography key={field} variant="body1">
                                            <strong>{field}:</strong> {errorMessage}
                                        </Typography>
                                    ))}
                                </div>

                            )}
                        </div>
                    </div>

                </motion.div>

            );
        }
        else {
            return (
                <motion.div
                    key={`section${currentSection}`}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={fanVariants}
                >
                    <div className={classes.fullPageLoader}>
                        <div className={classes.successContainer}>
                            <CheckCircle style={{ color: 'green', fontSize: 50 }} />
                            <Typography variant="h4" className={classes.successMessage}>
                                Matrícula enviada 🤩
                            </Typography>
                        </div>
                    </div>

                </motion.div>

            );
        }
    }


    return (
        <Container component="main" maxWidth="xs">

            <Typography variant="h3" align="center" color="textSecondary" paragraph>
                Formulario de Matrícula
            </Typography>
            <div className={classes.navigationContainer}>
                {Array.from({ length: totalSections }).map((_, index) => (
                    <div
                        key={index}
                        className={`${classes.navCircle} ${index + 1 === currentSection ? classes.activeCircle : ''}`}
                        onClick={() => setCurrentSection(index + 1)}
                    ></div>
                ))}
            </div>

            <motion.form onSubmit={handleSubmit}>
                <AnimatePresence>

                    {currentSection === 1 && (
                        <motion.div
                            key={`section${currentSection}`}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            variants={fanVariants}
                        >
                            {/* Datos personales */}
                            <TextField
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                id="nombre"
                                label="Nombre"
                                name="nombre"
                                value={alumnoData.nombre || ''}
                                onChange={(event) => setAlumnoData({ ...alumnoData, nombre: event.target.value })}
                                required
                                helperText={errors.nombre}
                                error={!!errors.nombre}
                            />
                            <TextField
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                id="pri_apellido"
                                label="Primer apellido"
                                name="pri_apellido"
                                value={alumnoData.pri_apellido || ''}
                                onChange={(event) => setAlumnoData({ ...alumnoData, pri_apellido: event.target.value })}
                                required
                                helperText={errors.pri_apellido}
                                error={!!errors.pri_apellido}
                            />
                            <TextField
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                id="seg_apellido"
                                label="Segundo apellido"
                                name="seg_apellido"
                                value={alumnoData.seg_apellido || ''}
                                onChange={(event) => setAlumnoData({ ...alumnoData, seg_apellido: event.target.value })}
                                required
                                helperText={errors.seg_apellido}
                                error={!!errors.seg_apellido}
                            />
                            <FormControl fullWidth variant="outlined" margin="normal">
                                <InputLabel id="tipo_doc-label">Tipo de Documento</InputLabel>

                                <Select
                                    labelId="tipo_doc-label"
                                    id="tipo_doc"
                                    name="tipo_doc"
                                    value={alumnoData.tipo_doc || ''}
                                    onChange={(event) => setAlumnoData({ ...alumnoData, tipo_doc: event.target.value })}
                                    label="Tipo de Documento"
                                    required



                                >
                                    <MenuItem value="">
                                        <em>Seleccione un tipo de documento</em>
                                    </MenuItem>
                                    {alumnoData.tipos_doc_choices && alumnoData.tipos_doc_choices.map(([value, label]) => (
                                        <MenuItem key={value} value={value}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.tipo_doc && <FormHelperText>{errors.tipo_doc}</FormHelperText>}
                            </FormControl>
                            <TextField
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                id="dni"
                                label="DNI"
                                name="dni"
                                value={alumnoData.dni || ''}
                                onChange={(event) => setAlumnoData({ ...alumnoData, dni: event.target.value })}
                                required
                                helperText={errors.dni}
                                error={!!errors.dni}
                            />
                            <TextField
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                id="fecha_nacimiento"
                                label="Fecha de Nacimiento"
                                name="fecha_nacimiento"
                                type="date"
                                value={alumnoData.fecha_nacimiento || ''}
                                onChange={(event) => setAlumnoData({ ...alumnoData, fecha_nacimiento: event.target.value })}
                                required
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                helperText={errors.fecha_nacimiento}
                                error={!!errors.fecha_nacimiento}
                            />
                            <Button
                                type="button"
                                fullWidth
                                variant="contained"
                                color="primary"
                                onClick={nextSection}
                                startIcon={<ArrowForward />}
                            >
                                Continuar
                            </Button>
                        </motion.div>
                    )}
                    {/* Contenido de la sección */}
                    {currentSection === 2 && (
                        <motion.div
                            key={`section${currentSection}`}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            variants={fanVariants}
                        >
                            {/* Datos de contacto */}
                            <TextField
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                id="telefono"
                                label="Teléfono"
                                name="telefono"
                                value={alumnoData.telefono || ''}
                                onChange={(event) => setAlumnoData({ ...alumnoData, telefono: event.target.value })}
                                required
                                helperText={errors.telefono}
                                error={!!errors.telefono}

                            />
                            <TextField
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                id="email"
                                label="Email"
                                name="email"
                                value={alumnoData.email || ''}
                                onChange={(event) => setAlumnoData({ ...alumnoData, email: event.target.value })}
                                required
                                helperText={errors.email}
                                error={!!errors.email}
                            />
                            <TextField
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                id="direccion"
                                label="Dirección"
                                name="direccion"
                                value={alumnoData.direccion || ''}
                                onChange={(event) => setAlumnoData({ ...alumnoData, direccion: event.target.value })}
                                required
                                helperText={errors.direccion}
                                error={!!errors.direccion}
                            />
                            <TextField
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                id="cod_postal"
                                label="Código Postal"
                                name="cod_postal"
                                value={alumnoData.cod_postal || ''}
                                onChange={(event) => setAlumnoData({ ...alumnoData, cod_postal: event.target.value })}
                                required
                                helperText={errors.cod_postal}
                                error={!!errors.cod_postal}

                            />
                            <FormControl fullWidth variant="outlined" margin="normal">
                                <InputLabel id="ciudad-label">Ciudad</InputLabel>
                                <Select
                                    labelId="ciudad-label"
                                    id="ciudad"
                                    name="ciudad"
                                    value={alumnoData.ciudad || ''}
                                    onChange={(event) => setAlumnoData({ ...alumnoData, ciudad: event.target.value })}
                                    label="Ciudad"
                                    required
                                >

                                    <MenuItem value="">
                                        <em>Seleccione su ciudad de residencia</em>
                                    </MenuItem>
                                    {ciudadData && ciudadData.map((ciudad) => (
                                        <MenuItem key={ciudad.id} value={ciudad.id}>
                                            {ciudad.nombre}
                                        </MenuItem>
                                    ))}

                                </Select>
                                {errors.ciudad && <FormHelperText>{errors.ciudad}</FormHelperText>}

                            </FormControl>
                            <FormControl fullWidth variant="outlined" margin="normal">
                                <InputLabel id="provincia-label">Provincia</InputLabel>
                                <Select
                                    labelId="provincia-label"
                                    id="provincia"
                                    name="provincia"
                                    value={alumnoData.provincia || ''}
                                    onChange={(event) => setAlumnoData({ ...alumnoData, provincia: event.target.value })}
                                    label="Provincia"
                                    required
                                >

                                    <MenuItem value="">
                                        <em>Seleccione su provincia de residencia</em>
                                    </MenuItem>
                                    {provinciaData && provinciaData.map((provincia) => (
                                        <MenuItem key={provincia.id} value={provincia.id}>
                                            {provincia.nombre}
                                        </MenuItem>
                                    ))}

                                </Select>
                                {errors.provincia && <FormHelperText>{errors.provincia}</FormHelperText>}
                            </FormControl>
                            <Button
                                type="button"
                                fullWidth
                                variant="contained"
                                color="secondary"
                                className="main"
                                onClick={prevSection}
                                startIcon={<ArrowBack />}
                            >
                                Atrás
                            </Button>
                            <Button
                                type="button"
                                fullWidth
                                variant="contained"
                                color="primary"
                                onClick={nextSection}
                                startIcon={<ArrowForward />}
                            >
                                Continuar
                            </Button>
                        </motion.div>
                    )}

                    {currentSection === 3 && (
                        <motion.div
                            key={`section${currentSection}`}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            variants={fanVariants}
                        >
                            {/* Datos bancarios */}
                            <FormControl fullWidth variant="outlined" margin="normal">
                                <InputLabel id="forma_de_pago-label">Forma de pago preferida</InputLabel>
                                <Select
                                    labelId="forma_de_pago-label"
                                    id="forma_de_pago"
                                    name="forma_de_pago"
                                    value={alumnoData.forma_de_pago || ''}
                                    onChange={(event) => setAlumnoData({ ...alumnoData, forma_de_pago: event.target.value })}
                                    label="Forma de Pago"
                                    required
                                >
                                    <MenuItem value="">
                                        <em>Seleccione una forma de pago</em>
                                    </MenuItem>
                                    {alumnoData.pagos_choices && alumnoData.pagos_choices.slice(0, -1).map(([value, label]) => (
                                        <MenuItem key={value} value={value}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.provincia && <FormHelperText>{errors.provincia}</FormHelperText>}
                            </FormControl>
                            {alumnoData.forma_de_pago === 3 && (
                                <>
                                    <TextField
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        id="titular_IBAN"
                                        label="Titular IBAN"
                                        name="titular_IBAN"
                                        value={alumnoData.titular_IBAN || ''}
                                        onChange={(event) => setAlumnoData({ ...alumnoData, titular_IBAN: event.target.value })}
                                        required
                                    />
                                    <TextField
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                        id="IBAN"
                                        label="IBAN"
                                        name="IBAN"
                                        value={alumnoData.IBAN || ''}
                                        onChange={(event) => setAlumnoData({ ...alumnoData, IBAN: event.target.value })}
                                        required
                                    />
                                </>
                            )}
                            <Button
                                type="button"
                                fullWidth
                                variant="contained"
                                color="secondary"
                                className="main"
                                onClick={prevSection}
                                startIcon={<ArrowBack />}
                            >
                                Atrás
                            </Button>
                            <Button
                                type="button"
                                fullWidth
                                variant="contained"
                                color="primary"
                                onClick={nextSection}
                                startIcon={<ArrowForward />}
                            >
                                Continuar
                            </Button>
                        </motion.div>
                    )}

                    {currentSection === 4 && (
                        <motion.div
                            key={`section${currentSection}`}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            variants={fanVariants}
                        >
                            {/* Personas de contacto */}

                            <TextField
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                id="persona_contacto"
                                label="Persona de Contacto"
                                name="persona_contacto"
                                value={alumnoData.persona_contacto || ''}
                                onChange={(event) => setAlumnoData({ ...alumnoData, persona_contacto: event.target.value })}
                            />
                            <TextField
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                id="telefono_contacto"
                                label="Teléfono de contacto"
                                name="telefono_contacto"
                                value={alumnoData.telefono_contacto || ''}
                                onChange={(event) => setAlumnoData({ ...alumnoData, telefono_contacto: event.target.value })}
                            />
                            <TextField
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                id="email_contacto"
                                label="Email de contacto"
                                name="email_contacto"
                                value={alumnoData.email_contacto || ''}
                                onChange={(event) => setAlumnoData({ ...alumnoData, email_contacto: event.target.value })}
                            />
                            <Button
                                type="button"
                                fullWidth
                                variant="contained"
                                color="secondary"
                                className="main"
                                onClick={prevSection}
                                startIcon={<ArrowBack />}
                            >
                                Atrás
                            </Button>
                            <Button
                                type="button"
                                fullWidth
                                variant="contained"
                                color="primary"
                                onClick={nextSection}
                                startIcon={<ArrowForward />}
                            >
                                Continuar
                            </Button>
                        </motion.div>
                    )}

                    {currentSection === 5 && (
                        <motion.div
                            key={`section${currentSection}`}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            variants={fanVariants}
                        >

                            {/* Datos académicos */}

                            <FormControl fullWidth variant="outlined" margin="normal">
                                <InputLabel id="tipo_carrera-label">Carrera actual</InputLabel>
                                <Select
                                    labelId="tipo_carrera-label"
                                    id="tipo_carrera"
                                    name="tipo_carrera"
                                    value={alumnoData.carrera || ''}
                                    onChange={(event) => setAlumnoData({ ...alumnoData, carrera: event.target.value })}
                                    label="Carrera actual"
                                    required
                                >
                                    <MenuItem value="">
                                        <em>Seleccione la carrera que esta cursando</em>
                                    </MenuItem>
                                    {carreraData && carreraData.map((carrera) => (
                                        <MenuItem key={carrera.id} value={carrera.id}>
                                            {carrera.nombre}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.carrera && <FormHelperText>{errors.carrera}</FormHelperText>}
                            </FormControl>

                            <FormControl fullWidth variant="outlined" margin="normal">
                                <InputLabel id="perfil_idioma-label">Perfil de idioma</InputLabel>
                                <Select
                                    labelId="perfil_idioma-label"
                                    id="perfil_idioma"
                                    name="Perfil de idioma"
                                    value={alumnoData.perfil || ''}
                                    onChange={(event) => setAlumnoData({ ...alumnoData, perfil: event.target.value })}
                                    label="Perfil de idioma"
                                    required
                                >
                                    <MenuItem value="">
                                        <em>Seleccione una forma de pago</em>
                                    </MenuItem>
                                    {perfilData && perfilData.map(([value, label]) => (
                                        <MenuItem key={value} value={value}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField

                                variant="outlined"
                                margin="normal"
                                fullWidth
                                id="curso"
                                label="Curso"
                                name="curso"
                                value={alumnoData.curso || ''}
                                onChange={(event) => setAlumnoData({ ...alumnoData, curso: event.target.value })}
                                required
                                type='number'
                                inputProps={{ min: 1, max: 6 }}
                                helperText={errors.curso}
                                error={!!errors.curso}

                            />

                            <Button
                                type="button"
                                fullWidth
                                variant="contained"
                                color="secondary"
                                className="main"
                                onClick={prevSection}
                                startIcon={<ArrowBack />}
                            >
                                Atrás
                            </Button>
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                                disabled={isSubmitting || isSubmitted}
                            >
                                {isSubmitting ? "Enviando..." : isSubmitted ? "Enviado" : "Enviar"}
                            </Button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.form>

        </Container>
    );


}

export default MatriculaForm;



